/**
 * Approvals API Client
 * API client for approval workflow operations
 */

import { apiClient } from './apiClient';

// Types
export interface ApprovalRequest {
  action_id: string;
  action_type: string;
  platform: string;
  campaign_name: string;
  campaign_id: string;
  requested_by: string;
  requester_name: string;
  requester_email: string;
  requested_at: string;
  status: 'pending_approval' | 'approved' | 'rejected' | 'expired';
  approval_level: 'auto' | 'manager' | 'director' | 'executive';
  estimated_impact: number;
  risk_level: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  parameters: Record<string, any>;
  approved_by?: string;
  approved_at?: string;
  rejection_reason?: string;
  expiration_at: string;
}

export interface ApprovalRule {
  rule_id: string;
  name: string;
  description: string;
  action_types: string[];
  min_amount?: number;
  max_amount?: number;
  platforms: string[];
  approval_level: string;
  approver_roles: string[];
  expiration_hours: number;
  priority: number;
  is_active: boolean;
}

export interface ApprovalStats {
  pending_count: number;
  approved_today: number;
  rejected_today: number;
  expired_today: number;
  avg_approval_time_hours: number;
}

// API Functions
export async function getPendingApprovals(
  organizationId: string
): Promise<ApprovalRequest[]> {
  const response = await apiClient.get<{ approvals: ApprovalRequest[] }>(
    `/api/v1/actions/pending-approval?organization_id=${organizationId}`
  );
  return response.data?.approvals || [];
}

export async function getApprovalHistory(
  organizationId: string,
  days: number = 30
): Promise<ApprovalRequest[]> {
  const response = await apiClient.get<{ approvals: ApprovalRequest[] }>(
    `/api/v1/actions/approval-history?organization_id=${organizationId}&days=${days}`
  );
  return response.data?.approvals || [];
}

export async function approveAction(
  actionId: string,
  approverId: string,
  comment?: string
): Promise<ApprovalRequest> {
  const response = await apiClient.post<ApprovalRequest>(
    `/api/v1/actions/${actionId}/approve`,
    { approver_id: approverId, comment }
  );
  return response.data;
}

export async function rejectAction(
  actionId: string,
  rejectorId: string,
  reason: string
): Promise<ApprovalRequest> {
  const response = await apiClient.post<ApprovalRequest>(
    `/api/v1/actions/${actionId}/reject`,
    { rejector_id: rejectorId, reason }
  );
  return response.data;
}

export async function bulkApprove(
  actionIds: string[],
  approverId: string,
  comment?: string
): Promise<{ success: string[]; failed: string[] }> {
  const response = await apiClient.post<{ success: string[]; failed: string[] }>(
    `/api/v1/actions/bulk-approve`,
    { action_ids: actionIds, approver_id: approverId, comment }
  );
  return response.data;
}

export async function bulkReject(
  actionIds: string[],
  rejectorId: string,
  reason: string
): Promise<{ success: string[]; failed: string[] }> {
  const response = await apiClient.post<{ success: string[]; failed: string[] }>(
    `/api/v1/actions/bulk-reject`,
    { action_ids: actionIds, rejector_id: rejectorId, reason }
  );
  return response.data;
}

export async function getApprovalRules(
  organizationId: string
): Promise<ApprovalRule[]> {
  const response = await apiClient.get<{ rules: ApprovalRule[] }>(
    `/api/v1/actions/auto-approval?organization_id=${organizationId}`
  );
  return response.data?.rules || [];
}

export async function getApprovalStats(
  organizationId: string
): Promise<ApprovalStats> {
  const response = await apiClient.get<ApprovalStats>(
    `/api/v1/actions/approval-stats?organization_id=${organizationId}`
  );
  return response.data;
}

// Helper functions
export function getRiskLevelColor(level: string): string {
  switch (level) {
    case 'critical': return 'bg-red-100 text-red-800';
    case 'high': return 'bg-orange-100 text-orange-800';
    case 'medium': return 'bg-yellow-100 text-yellow-800';
    case 'low': return 'bg-green-100 text-green-800';
    default: return 'bg-gray-100 text-gray-800';
  }
}

export function getApprovalLevelColor(level: string): string {
  switch (level) {
    case 'executive': return 'bg-purple-100 text-purple-800';
    case 'director': return 'bg-blue-100 text-blue-800';
    case 'manager': return 'bg-cyan-100 text-cyan-800';
    case 'auto': return 'bg-gray-100 text-gray-800';
    default: return 'bg-gray-100 text-gray-800';
  }
}

export function formatImpact(amount: number): string {
  if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(1)}M`;
  } else if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(1)}K`;
  }
  return `$${amount.toFixed(0)}`;
}

export function getTimeUntilExpiration(expirationAt: string): string {
  const now = new Date();
  const expiration = new Date(expirationAt);
  const diff = expiration.getTime() - now.getTime();
  
  if (diff <= 0) return 'Expired';
  
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  
  if (hours > 24) {
    return `${Math.floor(hours / 24)}d ${hours % 24}h`;
  } else if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}
